import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { API_URL, DEMO_VIDEO_URL } from "../config/env";
import { styles } from "../styles/appStyles";
import { track } from "../lib/analytics";

export default function AuthScreen({ onAuth, initialTab, onBack, onTryDemo }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const [authTab, setAuthTab] = useState(initialTab === "signup" ? "signup" : "login");
  const [authView, setAuthView] = useState("auth"); // "auth" | "forgot" | "check-email"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [hearAbout, setHearAbout] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Plan selection
  const [selectedPlan, setSelectedPlan] = useState(() => localStorage.getItem("pitcherly_selected_plan") || "");
  const selectPlan = (plan) => {
    track("signup_started", { target_plan: plan });
    localStorage.setItem("pitcherly_selected_plan", plan);
    setSelectedPlan(plan);
    setAuthTab("signup");
    setError(null);
    setSuccess(null);
    // Scroll to signup form
    const el = document.querySelector(".auth-right");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null); setSuccess(null);

    if (authTab === "signup") {
      if (password !== confirmPw) { setError("Passwords do not match."); setLoading(false); return; }
      const { data, error: err } = await supabase.auth.signUp({ email, password });
      if (err) { setError(err.message); setLoading(false); return; }
      if (data?.user?.identities?.length === 0) {
        setError("An account with this email already exists.");
        setLoading(false); return;
      }
      setSuccess("Check your email for a confirmation link, then log in.");
      track("signup_completed", { target_plan: selectedPlan || undefined });
      if (hearAbout) {
        fetch(`${API_URL}/track-signup-source`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_email: email, source: hearAbout }),
        }).catch(() => {});
      }
      setLoading(false);
    } else {
      const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) { setError(err.message); setLoading(false); return; }
      onAuth(data.session);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) { setError("Please enter your email."); return; }
    setLoading(true); setError(null);
    const { error: err } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${process.env.REACT_APP_BASE_URL || window.location.origin}/reset-password`,
    });
    if (err) { setError(err.message); setLoading(false); return; }
    setLoading(false);
    setAuthView("check-email");
  };

  const FEATURES = [
    { title: "MLS Listing Generator", desc: "professional copy in seconds" },
    { title: "Lead Follow-Up Emails", desc: "3 personalized emails per lead" },
    { title: "Contract Summarizer", desc: "Breaks down contracts into clear, simple language" },
    { title: "Open House Follow-Up", desc: "cycle through every visitor fast" },
    { title: "Seller Update Emails", desc: "weekly updates that retain clients" },
    { title: "Agent Bio Generator", desc: "full and short bio in one click" },
    { title: "CMA Generator", desc: "win more listing presentations" },
    { title: "Property Info", desc: "look up any property and save details instantly" },
    { title: "Contacts & CRM", desc: "manage leads, import CSV, track every client" },
    { title: "Email Export", desc: "send directly via Gmail, Outlook, or any email client" },
  ];

  return (
    <>
      <style>{styles}</style>

      <div
        className="auth-page"
        style={isMobile ? {
          display: "block",
          width: "100%",
          maxWidth: "100vw",
          minHeight: "100vh",
          background: "var(--bg)",
          padding: 0,
          margin: 0,
          overflowX: "hidden",
        } : undefined}
      >

        {/* LEFT — PRODUCT SHOWCASE */}
        <div
          className="auth-left"
          style={isMobile ? { display: "none" } : undefined}
        >
          <div className="auth-logo">Pitcher<em>ly</em></div>
          <div className="auth-hero">The AI writing toolkit built for real estate agents</div>
          <div className="auth-subhero">
            Save 5-10 hours a week on listings, emails, and admin. Pays for itself with one extra deal.
          </div>

          <div className="auth-features">
            {FEATURES.map(f => (
              <div className="auth-feature" key={f.title}>
                <span className="auth-feature-icon">✦</span>
                <span><strong>{f.title}</strong> — {f.desc}</span>
              </div>
            ))}
          </div>

          {DEMO_VIDEO_URL && (
            <div className="auth-demo">
              <div className="auth-demo-label">See it in action</div>
              <div className="auth-demo-player">
                <iframe
                  src={DEMO_VIDEO_URL}
                  title="Pitcherly Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="auth-demo-caption">2 minute demo · No signup required</div>
            </div>
          )}

          <div className="auth-pricing" id="pricing-section">
            <div className="auth-price-card" style={{opacity:0.92,borderColor:"rgba(255,255,255,0.06)"}}>
              <div className="auth-price-name">Lite</div>
              <div className="auth-price-amount">$19<span>/month</span></div>
              <ul className="auth-price-list">
                <li>50 generations / month</li>
                <li>All 10 writing tools</li>
                <li>No property lookups</li>
              </ul>
              <button className="auth-price-cta starter" onClick={() => selectPlan("lite")}>Start Free Trial &rarr;</button>
              <div className="auth-price-sub">7 days free &middot; then $19/mo &middot; cancel anytime</div>
            </div>
            <div className="auth-price-card">
              <div className="auth-price-name">Starter</div>
              <div className="auth-price-amount">$49<span>/month</span></div>
              <ul className="auth-price-list">
                <li>200 generations / month</li>
                <li>All 10 writing tools</li>
                <li>10 verified property lookups / month</li>
                <li>Market-data CMAs (live estimate + comps)</li>
                <li>Contacts & CRM with CSV import</li>
                <li>Email export (Gmail / Outlook)</li>
              </ul>
              <button className="auth-price-cta starter" onClick={() => selectPlan("starter")}>Start Free Trial &rarr;</button>
              <div className="auth-price-sub">7 days free &middot; then $49/mo &middot; cancel anytime</div>
            </div>
            <div className="auth-price-card featured">
              <div className="auth-price-badge">Most Popular</div>
              <div className="auth-price-name">Pro</div>
              <div className="auth-price-amount">$99<span>/month</span></div>
              <ul className="auth-price-list">
                <li>Unlimited generations</li>
                <li>All 10 writing tools</li>
                <li>50 verified property lookups / month</li>
                <li>Market-data CMAs (live estimate + comps)</li>
                <li>Priority support</li>
                <li>Early access to new features</li>
              </ul>
              <button className="auth-price-cta pro" onClick={() => selectPlan("pro")}>Start Free Trial &rarr;</button>
              <div className="auth-price-sub">7 days free &middot; then $99/mo &middot; cancel anytime</div>
            </div>
          </div>
          <div className="auth-plan-hint">Not sure which plan? Start with Starter — upgrade anytime.</div>

          <div className="auth-support">
            <div className="auth-support-title">Questions? We're here to help.</div>
            <a className="auth-support-email" href="mailto:support@pitcherly.com">support@pitcherly.com</a>
            <div className="auth-support-note">Typical response time: under 2 hours</div>
          </div>
        </div>

        {/* RIGHT — AUTH FORM */}
        <div
          className="auth-right"
          style={isMobile ? {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
            maxWidth: "100vw",
            minHeight: "100vh",
            padding: "80px 16px 32px",
            boxSizing: "border-box",
          } : undefined}
        >
          <div
            className="auth-card"
            style={isMobile ? {
              width: "100%",
              maxWidth: 420,
              margin: "0 auto",
              padding: 24,
              boxSizing: "border-box",
            } : undefined}
          >
            {authView === "auth" && (
              <>
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--text2)",
                      fontSize: 13,
                      cursor: "pointer",
                      padding: 0,
                      marginBottom: 12,
                      fontFamily: "'Geist', sans-serif",
                    }}
                  >
                    ← Back to home
                  </button>
                )}
                <div className="auth-tabs">
                  <button className={`auth-tab ${authTab === "login" ? "active" : ""}`} onClick={() => { setAuthTab("login"); setError(null); setSuccess(null); }}>Log In</button>
                  <button className={`auth-tab ${authTab === "signup" ? "active" : ""}`} onClick={() => { setAuthTab("signup"); setError(null); setSuccess(null); }}>Sign Up</button>
                </div>
                {authTab === "signup" && selectedPlan && (
                  <div className="auth-plan-banner">
                    You selected the {selectedPlan === "pro" ? "Pro" : selectedPlan === "lite" ? "Lite" : "Starter"} plan &middot; 7-day free trial included
                  </div>
                )}
                {error && <div className="auth-error">{error}</div>}
                {success && <div className="auth-success">{success}</div>}
                <form onSubmit={handleSubmit}>
                  <div className="auth-field">
                    <label htmlFor="auth-email">Email</label>
                    <input id="auth-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
                  </div>
                  <div className="auth-field">
                    <label htmlFor="auth-password">Password</label>
                    <input id="auth-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" required minLength={6} />
                  </div>
                  {authTab === "signup" && (
                    <div className="auth-field auth-signup-anim">
                      <label htmlFor="auth-confirm">Confirm Password</label>
                      <input id="auth-confirm" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Confirm password" required minLength={6} />
                    </div>
                  )}
                  {authTab === "signup" && (
                    <div className="auth-field auth-signup-anim">
                      <label htmlFor="auth-hear">How did you hear about us?</label>
                      <select id="auth-hear" value={hearAbout} onChange={e => setHearAbout(e.target.value)} style={{ width: "100%", padding: "10px 12px", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontSize: 14, fontFamily: "'Geist', sans-serif", appearance: "none", WebkitAppearance: "none", cursor: "pointer", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}>
                        <option value="" disabled>Select one...</option>
                        <option value="Instagram or TikTok">Instagram or TikTok</option>
                        <option value="Facebook Group">Facebook Group</option>
                        <option value="Friend or Colleague">Friend or Colleague</option>
                        <option value="Google Search">Google Search</option>
                        <option value="Reddit">Reddit</option>
                        <option value="YouTube">YouTube</option>
                        <option value="Text or DM">Text or DM</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  )}
                  {authTab === "signup" && (
                    <div className="auth-signup-anim" style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      gap: 10,
                      width: "100%",
                      margin: "12px 0",
                      boxSizing: "border-box",
                    }}>
                      <input
                        type="checkbox"
                        id="terms-agree"
                        checked={agreeTerms}
                        onChange={e => setAgreeTerms(e.target.checked)}
                        style={{
                          width: 18,
                          height: 18,
                          minWidth: 18,
                          minHeight: 18,
                          marginTop: 2,
                          flexShrink: 0,
                          accentColor: "#d4915c",
                          cursor: "pointer",
                        }}
                      />
                      <label
                        htmlFor="terms-agree"
                        style={{
                          fontSize: 13,
                          color: "#888",
                          lineHeight: 1.5,
                          cursor: "pointer",
                          flex: 1,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          whiteSpace: "normal",
                          maxWidth: "calc(100% - 28px)",
                        }}
                      >
                        I agree to the{" "}
                        <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#d4915c", textDecoration: "underline" }}>Terms of Service</a>
                        {" "}and{" "}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#d4915c", textDecoration: "underline" }}>Privacy Policy</a>
                      </label>
                    </div>
                  )}
                  <button className="auth-btn" type="submit" disabled={loading || (authTab === "signup" && !agreeTerms)}>
                    {loading ? "Please wait..." : authTab === "login" ? "Log In" : "Create Account"}
                  </button>
                </form>
                <div className="auth-fine">7-day free trial. No commitment. Cancel anytime.</div>
                {authTab === "login" && (
                  <button className="auth-forgot" type="button" onClick={() => { setAuthView("forgot"); setError(null); setSuccess(null); setResetEmail(email); }}>
                    Forgot password?
                  </button>
                )}
                {onTryDemo && (
                  <div style={{
                    marginTop: 16,
                    paddingTop: 16,
                    borderTop: "1px solid var(--border)",
                    textAlign: "center",
                  }}>
                    <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 8 }}>
                      Just want to look around?
                    </div>
                    <button
                      type="button"
                      onClick={onTryDemo}
                      style={{
                        background: "transparent",
                        border: "1px solid var(--border)",
                        color: "var(--text)",
                        padding: "10px 18px",
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: "'Geist', sans-serif",
                        width: "100%",
                      }}
                    >
                      Explore as Guest (No Signup)
                    </button>
                  </div>
                )}
              </>
            )}

            {authView === "forgot" && (
              <>
                <button className="auth-back" onClick={() => { setAuthView("auth"); setError(null); }}>
                  ← Back to login
                </button>
                <div className="auth-card-title">Reset your password</div>
                <div className="auth-card-sub">Enter the email address associated with your account and we'll send you a link to reset your password.</div>
                {error && <div className="auth-error">{error}</div>}
                <form onSubmit={handleForgotPassword}>
                  <div className="auth-field">
                    <label>Email</label>
                    <input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} placeholder="you@example.com" required />
                  </div>
                  <button className="auth-btn" type="submit" disabled={loading}>
                    {loading ? "Sending..." : "Send Reset Link"}
                  </button>
                </form>
              </>
            )}

            {authView === "check-email" && (
              <div style={{ textAlign: "center" }}>
                <div className="auth-check-icon">✓</div>
                <div className="auth-card-title">Check your email</div>
                <div className="auth-card-sub">We sent a password reset link to:</div>
                <div className="auth-check-email">{resetEmail}</div>
                <div className="auth-spam-note">Didn't get it? Check your spam folder or try again.</div>
                <button className="auth-back-link" onClick={() => { setAuthView("auth"); setError(null); }}>
                  ← Back to login
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="auth-see-plans"
            onClick={() => {
              window.dispatchEvent(new CustomEvent("pitcherly-open-menu", { detail: { tab: "why" } }));
            }}
          >
            Learn more about Pitcherly ☰
          </button>
        </div>

      </div>
      <div style={{textAlign:"center",padding:"24px 16px 8px",fontSize:11,color:"#555"}}>
        &copy; 2026 Pitcherly &middot; Brea, CA 92821 &middot; <a href="mailto:support@pitcherly.com" style={{color:"#555",textDecoration:"none"}}>support@pitcherly.com</a>
      </div>
      <div style={{textAlign:"center",padding:"0 16px 32px",fontSize:11,color:"#555"}}>
        <a href="/terms" style={{color:"#555",textDecoration:"none"}}>Terms of Service</a> &middot; <a href="/privacy" style={{color:"#555",textDecoration:"none"}}>Privacy Policy</a>
      </div>
    </>
  );
}
